import { formatDate } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ChartType } from 'angular-google-charts';
import { Subscription } from 'rxjs';
import { API_BASE_URL_SERVICE } from '../../../../projects/kiene-core/src/lib/kiene-core.config';
import { MessageService } from '../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneTableService } from '../../components/kiene-table/kiene-table.service';
import { CsvColumn } from '../../csv-export/csv-export/csv-column';
import { ZeitauswahlChange } from '../zeitauswahl/zeitauswahl-change';
import { FahrzeugKostenGesamt, FahrzeugKostenSummen } from './fahrzeugkosten-gesamt';

@Component({
    selector: 'kiene-fahrzeugkosten-report',
    templateUrl: './fahrzeugkosten-report.component.html',
    styleUrls: ['./fahrzeugkosten-report.component.scss'],
})
export class FahrzeugkostenReportComponent implements OnInit, AfterViewInit {
    displayedColumns = [
        'kennzeichen',
        'bezeichnung',
        'tierarzt',
        'kostenstelle_suffix',
        'anteil_prozent',
        'netto_4510',
        'netto_4520',
        'netto_4530',
        'netto_4540',
        'netto_4580',
        'netto_2120',
        'netto_sonst',
        'netto_gesamt',
        'belege',
    ];

    dataSource = new MatTableDataSource<FahrzeugKostenGesamt>([]);
    @ViewChild(MatSort) sort: MatSort;

    private allRecords: FahrzeugKostenGesamt[] = [];
    zeiteintrag: ZeitauswahlChange;
    summen: FahrzeugKostenSummen | null = null;
    loading = false;
    sub: Subscription;
    nurFreigegebenCtrl = new FormControl(false);
    auchOhneKostenCtrl = new FormControl(false);

    chartType = ChartType.PieChart;
    chartColumns = ['Fahrzeug', 'Kosten'];
    chartData: (string | number)[][] = [['', 0]];
    chartHasData = false;

    csvColumns: CsvColumn[] = [];
    csvFilename = 'Fahrzeugkosten.csv';

    constructor(
        private readonly api: KieneTableService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.csvColumns = [
            new CsvColumn('kennzeichen', 'Kennzeichen'),
            new CsvColumn('bezeichnung', 'Bezeichnung'),
            new CsvColumn('tierarzt', 'Tierarzt'),
            new CsvColumn('kostenstelle_suffix', 'KST-Suffix'),
            new CsvColumn('anteil_prozent', 'Anteil (%)', '%'),
            new CsvColumn('netto_4510', '4510 Steuer'),
            new CsvColumn('netto_4520', '4520 Versicherung'),
            new CsvColumn('netto_4530', '4530 Betrieb'),
            new CsvColumn('netto_4540', '4540 Reparatur'),
            new CsvColumn('netto_4580', '4580 Sonstige/Connectivity'),
            new CsvColumn('netto_2120', '2120 Zinsen'),
            new CsvColumn('netto_sonst', 'Sonstige Konten'),
            new CsvColumn('netto_gesamt', 'Gesamt'),
            new CsvColumn('belege', 'Belege'),
            new CsvColumn('positionen', 'Positionen'),
        ];
        this.nurFreigegebenCtrl.valueChanges.subscribe(() => this.loadData());
        this.auchOhneKostenCtrl.valueChanges.subscribe(() => this.applyFilter());
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.sortingDataAccessor = (row, col) => {
            const v = (row as any)[col];
            return typeof v === 'string' ? v.toLowerCase() : v;
        };
    }

    zeitintervalChanged(event: ZeitauswahlChange): void {
        this.zeiteintrag = event;
        this.loadData();
    }

    loadData(): void {
        if (!this.zeiteintrag?.von || !this.zeiteintrag?.bis) {
            return;
        }
        let params = new HttpParams();
        params = params.set('von', formatDate(this.zeiteintrag.von, 'yyyy-MM-dd', 'de'));
        params = params.set('bis', formatDate(this.zeiteintrag.bis, 'yyyy-MM-dd', 'de'));
        params = params.set('nur_freigegeben', this.nurFreigegebenCtrl.value ? '1' : '0');

        this.loading = true;
        this.sub?.unsubscribe();
        this.sub = this.api.get(this.apiBaseUrl + 'report/read_fahrzeug_gesamt.php', params).subscribe({
            next: (res: any) => {
                this.allRecords = res?.records ?? [];
                this.summen = res?.summen ?? null;
                this.applyFilter();
                this.loading = false;
            },
            error: err => {
                this.loading = false;
                this.messageService.errorMessage('Fahrzeugkosten konnten nicht geladen werden.', err);
            },
        });
    }

    private applyFilter(): void {
        const showAll = !!this.auchOhneKostenCtrl.value;
        this.dataSource.data = showAll
            ? this.allRecords
            : this.allRecords.filter(r => (Number(r.netto_gesamt) || 0) > 0);

        this.chartData = [];
        for (const r of this.dataSource.data) {
            const n = Math.abs(Number(r.netto_gesamt) || 0);
            if (n <= 0) {
                continue;
            }
            const label = r.kennzeichen || r.anzeige || String(r.fibu_fahrzeug_id);
            this.chartData.push([label, n]);
        }
        this.chartHasData = this.chartData.length > 0;
        if (!this.chartHasData) {
            this.chartData = [['Keine Kosten', 0]];
        }
    }
}
